/**
 * Preferences service API calls.
 *
 * This module provides functions for user language preferences management
 * including fetching, updating preferences, and listing supported languages.
 */

import apiClient from './api';

/**
 * Get current user's language preferences
 * @returns {Promise<Object>} Preferences with preferred_language and readable_languages
 */
export const getUserPreferences = async () => {
  try {
    const response = await apiClient.get('/api/preferences/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch preferences'
    );
  }
};

/**
 * Update user's language preferences
 * @param {Object} data - Preferences update data
 * @param {string} [data.preferred_language] - ISO 639-1 preferred language code
 * @param {string[]} [data.readable_languages] - ISO 639-1 readable language codes
 * @returns {Promise<Object>} Updated preferences
 */
export const updateUserPreferences = async (data) => {
  try {
    const response = await apiClient.put('/api/preferences/', data);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to update preferences'
    );
  }
};

/**
 * Get list of supported languages
 * @returns {Promise<Object>} Languages list with code and display name
 */
export const getSupportedLanguages = async () => {
  try {
    const response = await apiClient.get('/api/preferences/languages');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch supported languages'
    );
  }
};
